/**
 * Local development authentication bypass.
 *
 * In production the BPMN editor authenticates against Microsoft Entra via
 * MSAL. Locally there is no Entra client ID, so this module provides a stub
 * MSAL instance with a fixed user ("Robert Stockamp") that satisfies the
 * `useMsal()` consumers (App, Toolbar, VideoPlayer, apiClient) without a real
 * login.
 *
 * It only activates when REACT_APP_ENTRA_CLIENT_ID is missing AND the build is
 * a development build (or REACT_APP_ALLOW_UNAUTHENTICATED_DEV=true is set), so
 * a misconfigured production build still fails loudly instead of silently
 * running unauthenticated.
 */

import { InteractionStatus } from '@azure/msal-browser';

export const DEV_USER_NAME = 'Robert Stockamp';
export const DEV_USER_EMAIL = 'robert.stockamp@weissenberg-group.de';

export function isDevAuthBypass() {
  const clientId = (process.env.REACT_APP_ENTRA_CLIENT_ID || '').trim();
  if (clientId) return false;
  return (
    process.env.REACT_APP_ALLOW_UNAUTHENTICATED_DEV === 'true' ||
    process.env.NODE_ENV === 'development'
  );
}

export const devAccount = {
  homeAccountId: 'dev-local.robert-stockamp',
  localAccountId: 'dev-local-robert-stockamp',
  environment: 'login.windows.net',
  tenantId: 'dev-local-tenant',
  username: DEV_USER_EMAIL,
  name: DEV_USER_NAME,
  idTokenClaims: {
    name: DEV_USER_NAME,
    preferred_username: DEV_USER_EMAIL,
  },
};

const noopLogger = {
  info() {},
  verbose() {},
  warning() {},
  error() {},
  trace() {},
  errorPii() {},
  warningPii() {},
  infoPii() {},
  verbosePii() {},
  tracePii() {},
  clone() {
    return noopLogger;
  },
};

/**
 * Minimal PublicClientApplication-shaped stub. Only implements the surface the
 * editor actually calls. Token methods return a dummy token because the local
 * backend runs with ALLOW_UNAUTHENTICATED_DEV and does not validate it.
 */
function createStubInstance() {
  let activeAccount = devAccount;
  const devAuthResult = {
    account: devAccount,
    accessToken: 'dev-local-token',
    idToken: 'dev-local-id-token',
    scopes: [],
    expiresOn: new Date(Date.now() + 60 * 60 * 1000),
  };

  return {
    initialize: () => Promise.resolve(),
    getActiveAccount: () => activeAccount,
    setActiveAccount: (account) => {
      activeAccount = account || devAccount;
    },
    getAllAccounts: () => [devAccount],
    getAccountByHomeId: () => devAccount,
    getAccountByLocalId: () => devAccount,
    getAccountByUsername: () => devAccount,
    handleRedirectPromise: () => Promise.resolve(null),
    acquireTokenSilent: () => Promise.resolve(devAuthResult),
    acquireTokenPopup: () => Promise.resolve(devAuthResult),
    loginPopup: () => Promise.resolve(devAuthResult),
    loginRedirect: () => Promise.resolve(),
    logoutRedirect: () => Promise.resolve(),
    logoutPopup: () => Promise.resolve(),
    ssoSilent: () => Promise.resolve(devAuthResult),
    addEventCallback: () => null,
    removeEventCallback: () => {},
    enableAccountStorageEvents: () => {},
    disableAccountStorageEvents: () => {},
    getLogger: () => noopLogger,
    setLogger: () => {},
    getConfiguration: () => ({ auth: { clientId: 'dev-local' } }),
  };
}

/** Build the value object expected by `MsalContext.Provider` / `useMsal()`. */
export function createDevMsalContextValue() {
  return {
    instance: createStubInstance(),
    inProgress: InteractionStatus.None,
    accounts: [devAccount],
    logger: noopLogger,
  };
}
